import { AlertCircle, CheckCircle2, CircleSlash, Clock, RefreshCw } from "lucide-react";
import type { SyncSchedule } from "@/types";

export interface CollectionFile {
  uuid: string;
  filename: string;
  fileSize: number | null;
  mimeType: string | null;
  status: string;
  errorMessage: string | null;
  sourceType: string | null;
  sourceUrl: string | null;
  createdAt: string;
}

export function getStatusConfig(status: string, errorMessage?: string | null) {
  switch (status) {
    case "indexed":
      return {
        icon: CheckCircle2,
        color: "text-green-400",
        bg: "bg-green-500/20",
        label: "Indexed",
      };
    case "processing":
      return {
        icon: RefreshCw,
        color: "text-blue-400",
        bg: "bg-blue-500/20",
        label: "Processing",
        animate: true,
      };
    case "error":
    case "failed": {
      // Check if this is a "too large" error — file is uploaded but not indexable
      const isTooLarge = errorMessage?.toLowerCase().includes("too large");
      if (isTooLarge) {
        return {
          icon: AlertCircle,
          color: "text-yellow-400",
          bg: "bg-yellow-500/20",
          label: "Not Indexed",
          tooltip:
            "This file exceeds the maximum token limit for search indexing. It is still available in the data source for use with other tools.",
        };
      }
      return {
        icon: AlertCircle,
        color: "text-red-400",
        bg: "bg-red-500/20",
        label: "Error",
        tooltip: errorMessage || undefined,
      };
    }
    case "pending":
      return {
        icon: Clock,
        color: "text-yellow-400",
        bg: "bg-yellow-500/20",
        label: "Pending",
      };
    case "skipped":
      return {
        icon: CircleSlash,
        color: "text-content-quaternary",
        bg: "bg-surface-tertiary/20",
        label: "Skipped",
        tooltip: errorMessage || "File type not supported for indexing",
      };
    default:
      return {
        icon: Clock,
        color: "text-content-tertiary",
        bg: "bg-surface-tertiary/20",
        label: status,
      };
  }
}

export function getSourceTypeLabel(sourceType: string | null) {
  switch (sourceType) {
    case "upload":
      return "Uploaded";
    case "website":
      return "Website";
    case "sharepoint":
      return "SharePoint";
    case "notes":
      return "Note";
    default:
      return null;
  }
}

export function formatFileSize(bytes: number | null) {
  if (!bytes) return "Unknown";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function hasMarkdownVersion(file: CollectionFile): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  const ext = file.filename.includes(".") ? file.filename.slice(file.filename.lastIndexOf(".")).toLowerCase() : "";
  // Files that are already text/markdown don't need a separate version
  if (mime === "text/markdown" || mime === "text/plain" || ext === ".md" || ext === ".txt") return false;
  // JSON files are already readable
  if (mime === "application/json" || ext === ".json") return false;
  return file.status === "indexed";
}

export function isValidUrl(value: string): boolean {
  if (!value.trim()) return false;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/** Determine whether a visibility change is more restrictive. */
export function checkIsMoreRestrictive(oldVis: string, newVis: string, teamChanged: boolean): boolean {
  return (
    (oldVis === "public" && newVis !== "public") ||
    (oldVis === "team" && newVis === "private") ||
    (oldVis === "team" && newVis === "team" && teamChanged)
  );
}

/** Build a human-readable impact message from the visibility impact response. */
export function buildImpactMessage(impact: {
  affectedAgents: unknown[];
  affectedFlows: unknown[];
  affectedProjects?: unknown[];
  totalAffected: number;
  warningMessage?: string;
}): string {
  if (impact.warningMessage) {
    return impact.warningMessage;
  }
  const parts: string[] = [];
  const agentCount = impact.affectedAgents.length;
  const flowCount = impact.affectedFlows.length;
  const projectCount = impact.affectedProjects?.length ?? 0;
  if (agentCount > 0) parts.push(`${agentCount} agent${agentCount === 1 ? "" : "s"}`);
  if (flowCount > 0) parts.push(`${flowCount} flow${flowCount === 1 ? "" : "s"}`);
  if (projectCount > 0) parts.push(`${projectCount} project${projectCount === 1 ? "" : "s"}`);
  return `Changing visibility may affect ${parts.join(" and ")} that use this data source. These entities will remain linked, but users who lose access will no longer receive search results from this data source.`;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Human-readable description of an automated sync cadence, or a manual-only note. */
export function formatSyncCadence(syncSchedule?: SyncSchedule | null): string {
  if (!syncSchedule) {
    return "Syncs in the background when triggered (no fixed schedule).";
  }
  const tz = syncSchedule.timezone ? ` (${syncSchedule.timezone})` : "";
  const at = syncSchedule.run_at_time ? ` at ${syncSchedule.run_at_time}${tz}` : "";
  switch (syncSchedule.type) {
    case "weekly": {
      const days = (syncSchedule.days_of_week ?? [])
        .map((d) => DAY_LABELS[d] ?? "")
        .filter(Boolean)
        .join(", ");
      return days ? `Syncs weekly on ${days}${at}.` : `Syncs weekly${at}.`;
    }
    case "custom": {
      const n = syncSchedule.interval_days ?? 1;
      return n > 1 ? `Syncs every ${n} days${at}.` : `Syncs daily${at}.`;
    }
    default:
      return `Syncs daily${at}.`;
  }
}
